// src/webrtc/voiceReceiver.js
import { rtdb } from "../firebase";
import { ref as rtdbRef, onValue, set, remove, push, get, update, onChildAdded } from "firebase/database";

let localStream;
let peerConnection;
let unsubCallListener;
let unsubCandidateListener;

export async function answerIncomingCall(convId, currentUid, onLocalStream, onRemoteStream, onCallEnd) {
  const callRef = rtdbRef(rtdb, `calls/${convId}`);
  const snapshot = await get(callRef);
  const data = snapshot.val();

  if (!data || !data.offer) {
    console.warn("Call offer not found. Call might have been cancelled.");
    return "MISSING";
  }

  // Ensure clean slate
  cleanup();

  const servers = {
    iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
  };

  peerConnection = new RTCPeerConnection(servers);

  // Determines if we should enable video based on caller signal
  const isVideo = data.isVideo || false;

  try {
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: isVideo ? true : false
    });
    // Notify UI of local stream immediately
    if (onLocalStream) onLocalStream(localStream);
  } catch (err) {
    console.error("Camera/Mic error", err);
    // Proceed without media? No, call fails.
    return "MEDIA_ERROR";
  }

  if (localStream && peerConnection) {
    localStream.getTracks().forEach((track) => {
      if (peerConnection) peerConnection.addTrack(track, localStream);
    });
  }

  // Capture Remote Stream
  if (peerConnection) {
    peerConnection.ontrack = (event) => {
      if (onRemoteStream && event.streams && event.streams[0]) {
        onRemoteStream(event.streams[0]);
      }
    };

    peerConnection.oniceconnectionstatechange = () => {
      // Connection State Monitoring
    };
  }

  // Add remote ICE candidates (Improved with onChildAdded)
  const candidateQueue = [];
  const offerCandidatesRef = rtdbRef(rtdb, `calls/${convId}/offerCandidates`);

  if (unsubCandidateListener) unsubCandidateListener();
  unsubCandidateListener = onChildAdded(offerCandidatesRef, (snap) => {
    if (!peerConnection) return;

    const data = snap.val();
    const c = data?.candidate ? data : { candidate: data };

    if (c && c.candidate) {
      if (peerConnection && peerConnection.remoteDescription && peerConnection.signalingState !== 'closed') {
        peerConnection.addIceCandidate(new RTCIceCandidate(c.candidate)).catch(e => { });
      } else {
        candidateQueue.push(c.candidate);
      }
    }
  });

  // Add local ICE candidates
  const answerCandidatesRef = rtdbRef(rtdb, `calls/${convId}/answerCandidates`);
  if (peerConnection) {
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateRef = push(answerCandidatesRef);
        set(candidateRef, {
          uid: currentUid,
          candidate: event.candidate.toJSON()
        });
      }
    };
  }

  if (peerConnection) {
    await peerConnection.setRemoteDescription(new RTCSessionDescription(data.offer));

    // Flush queue
    while (candidateQueue.length > 0) {
      const c = candidateQueue.shift();
      try {
        if (peerConnection) await peerConnection.addIceCandidate(new RTCIceCandidate(c));
      } catch (e) { console.warn("Failed to add queued candidate", e); }
    }

    const answer = await peerConnection.createAnswer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true
    });
    await peerConnection.setLocalDescription(answer);

    // ✅ Set Status Connected
    await update(callRef, {
      answer: {
        type: answer.type,
        sdp: answer.sdp,
      },
      status: 'connected'
    });
  }

  // ✅ Listen for Status Changes (End/Reject)
  if (unsubCallListener) unsubCallListener();
  unsubCallListener = onValue(callRef, (snapshot) => {
    const d = snapshot.val();
    if (!d || d.status === 'ended' || d.status === 'rejected') {
      cleanup();
      if (onCallEnd) onCallEnd("ENDED");
    }
  });

  return localStream;
}

function cleanup() {
  if (unsubCallListener) {
    unsubCallListener();
    unsubCallListener = null;
  }
  if (unsubCandidateListener) {
    unsubCandidateListener();
    unsubCandidateListener = null;
  }

  if (peerConnection) {
    peerConnection.onicecandidate = null;
    peerConnection.ontrack = null;
    peerConnection.close();
    peerConnection = null;
  }
  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    localStream = null;
  }
}

export function rejectCall(convId) {
  const callRef = rtdbRef(rtdb, `calls/${convId}`);
  update(callRef, { status: 'rejected' }).then(() => {
    setTimeout(() => remove(callRef), 2000);
  });
  cleanup(); // Ensure local cleanup
}

export function endCall(convId) {
  cleanup();
  if (convId) {
    const callRef = rtdbRef(rtdb, `calls/${convId}`);
    update(callRef, { status: 'ended' }).then(() => {
      setTimeout(() => remove(callRef), 2000);
    });
  }
}
